import { FC, useRef, useState } from 'react';
import { Grid, IconButton, Tooltip, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@material-ui/icons/Delete';
import BlockIcon from '@material-ui/icons/Block';
import AddCircle from '@material-ui/icons/AddCircle';
import { useDispatch } from 'react-redux';
import { useStyles } from './styles';
import BlockUserDialog from '../../../BlockUserDialog';
import RemoveUserMessageDialog from '../../../RemoveUserMessageDialog';
import { thunkToggleBlockUser } from '../../../../../../store/actions/Streaming.actions';

interface ChatMessageProps {
    avatar: string;
    message: string;
    username: string;
    isBanned: boolean;
    isInfluencer: boolean;
    isEventOwner: boolean;
    hasAdminPermissions?: boolean;
    mobileLayout?: boolean;
}

const OVERLY_TIMER = 2 /* seconds */ * 1000; /* milliseconds */

const ChatMessage: FC<ChatMessageProps> = ({
    avatar,
    message,
    username,
    isInfluencer,
    mobileLayout,
    hasAdminPermissions,
    isEventOwner,
    isBanned,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [showMessageOptions, setShowMessageOptions] = useState<boolean>(false);
    const [showBlockModal, setShowBlockModal] = useState<boolean>(false);
    const [showRemoveMessageModal, setShowRemoveMessageModal] = useState<boolean>(false);
    const idleState = useRef<boolean>();
    const idleTimer = useRef<number>();

    const triggerShowOverlay = (time: number) => {
        try {
            clearTimeout(idleTimer.current);
        } catch (e) {}
        if (!showMessageOptions) {
            setShowMessageOptions(true);
        }
        idleState.current = false;
        // @ts-ignore
        idleTimer.current = setTimeout(function () {
            setShowMessageOptions(false);
            idleState.current = true;
        }, time);
    };

    const toggleUserBlocked = () => {
        if (username) {
            dispatch(thunkToggleBlockUser(username));
        }
    };

    return (
        <Grid container xs={12} direction="column">
            {hasAdminPermissions && !isEventOwner && (
                <>
                    <BlockUserDialog
                        open={showBlockModal}
                        onDismiss={() => setShowBlockModal(false)}
                        user={username}
                        isBanned={isBanned}
                        onAcceptEvent={() => {
                            toggleUserBlocked();
                            setShowBlockModal(false);
                        }}
                    />

                    <RemoveUserMessageDialog
                        open={showRemoveMessageModal}
                        onDismiss={() => setShowRemoveMessageModal(false)}
                        user={username}
                        onAcceptEvent={() => {
                            setShowRemoveMessageModal(false);
                        }}
                    />
                </>
            )}

            <Grid
                container
                xs={12}
                justify="flex-start"
                alignContent="center"
                alignItems="center"
                direction="column"
                className={clsx(classes.container, !mobileLayout ? classes.desktopBackground : '')}
                onMouseOver={() => {
                    triggerShowOverlay(OVERLY_TIMER);
                }}
                onMouseMove={() => {
                    triggerShowOverlay(OVERLY_TIMER);
                }}
            >
                <Grid
                    container
                    xs={12}
                    justify="flex-start"
                    alignContent="center"
                    alignItems="center"
                    direction="row"
                >
                    <img
                        className={clsx(classes.avatar, isInfluencer ? classes.influencerDecoration : '')}
                        src={avatar}
                        alt={t('{{username}} user avatar', { username })}
                    />
                    <Grid item className={clsx(classes.textContainer)}>
                        <Typography className={clsx(classes.text, classes.userName)}>{username}</Typography>
                        <Typography className={clsx(classes.text, classes.chatMessage)}>{message}</Typography>
                    </Grid>
                </Grid>
                {hasAdminPermissions && !isEventOwner && showMessageOptions && (
                    <Grid
                        container
                        xs={12}
                        justify="flex-end"
                        alignContent="center"
                        alignItems="center"
                        direction="row"
                        onMouseOver={() => {
                            triggerShowOverlay(OVERLY_TIMER);
                        }}
                        onMouseMove={() => {
                            triggerShowOverlay(OVERLY_TIMER);
                        }}
                    >
                        {false && (
                            <IconButton onClick={() => setShowRemoveMessageModal(true)}>
                                <DeleteIcon className={classes.icon} />
                            </IconButton>
                        )}
                        <Tooltip
                            // @ts-ignore
                            title={isBanned ? t('Unblock user') : t('Block user')}
                        >
                            <IconButton onClick={() => setShowBlockModal(true)}>
                                {isBanned ? (
                                    <AddCircle className={classes.icon} />
                                ) : (
                                    <BlockIcon className={classes.icon} />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};

export default ChatMessage;
